import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { salesApi } from '../../services/salesApi.js';
import { formatDate } from '../../utils/formatters.js';

import { ContractCreateModal } from './ContractCreateModal.jsx';

const STATUS_VARIANT = { ACTIVE: 'success', CANCELLED: 'neutral', EXPIRED: 'warning' };

export function ContractsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const list = useQuery({ queryKey: ['sales', 'contracts'], queryFn: () => salesApi.listContracts().then((r) => r.items) });
  const rows = list.data || [];
  const active = rows.filter((r) => r.status === 'ACTIVE').length;

  const cancel = useMutation({
    mutationFn: (id) => salesApi.cancelContract(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sales', 'contracts'] }),
  });

  const columns = [
    { key: 'contractNumber', label: t('sc.col.number'), render: (r) => <span className="mono">{r.contractNumber}</span> },
    { key: 'customer', label: t('sc.col.customer'), render: (r) => r.customerName || '-' },
    { key: 'title', label: t('sc.col.title') },
    { key: 'period', label: t('sc.col.period'), render: (r) => (r.startDate ? `${formatDate(r.startDate)} - ${r.endDate ? formatDate(r.endDate) : '…'}` : '-') },
    { key: 'status', label: t('col.status'), render: (r) => <Badge variant={STATUS_VARIANT[r.status] || 'neutral'}>{t(`sc.status.${r.status}`)}</Badge> },
    { key: 'actions', label: '', align: 'right', render: (r) => (r.status === 'ACTIVE' ? <Button size="sm" variant="ghost" disabled={cancel.isPending} onClick={() => cancel.mutate(r.id)}>{t('sc.action.cancel')}</Button> : null) },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('sc.title')}</h1>
          <p className="muted">{t('sc.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="primary" onClick={() => setOpen(true)}>{t('sc.action.new')}</Button>
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('sc.metric.total')} value={String(rows.length)} />
        <MetricCard label={t('sc.metric.active')} value={String(active)} trendVariant="success" />
      </div>

      <Table columns={columns} rows={rows} isLoading={list.isLoading} emptyMessage={t('sc.empty')} />

      <ContractCreateModal open={open} onClose={() => setOpen(false)} />
    </section>
  );
}
